import { AllyDomChange } from "../types";
import { AllyId } from "./ally-id.class";
import { AllyBase } from "./base";
import { AllyObject } from "./ally-object.class";
import { AllyCircularLoopProtection } from "./ally-circular-loop-protection.class";
import { AllyTools } from "./ally-tools.class";

/**
 * Objects of this class house MutationObserver.
 * 
 * @extends AllyBase
 * @see AllyObserver
 */
export class AllyObserverInstance extends AllyBase {
    /**
     * The id of this object.
     */
    private id: string;
    /**
     * The callback to be executed when an event is detected by the MutationObserver.
     */
    private callback: (changes: AllyDomChange[], observer: MutationObserver) => void;
    /**
     * The object on whose node the MutationObserver should listen.
     */
    private object: AllyObject;
    /**
     * The MutationObserver object of this AllyObserverInstance object.
     */
    private observer: MutationObserver;
    /**
     * The callback will only be executed when an event occurs if this property is True.
     */
    private observe: boolean;
    /**
     * If an attribute event occurs on this node then the property observe of this AllyObserverInstance object will be set to True.
     */
    private startNode: HTMLElement;
    /**
     * If an attribute event occurs on this node then the property observe of this AllyObserverInstance object will be set to False.
     */
    private stopNode: HTMLElement;

    private deCircularLoopProtection: AllyCircularLoopProtection;

    private deTools: AllyTools;

    /**
     * @param bridge The bridge to the parent in the hierarchical tree.
     * @param ids The AllyId object provided by the AllyObserver object.
     * @param callback The callback to be executed when an event is detected by the MutationObserver.
     * @param object The object on whose node the MutationObserver should listen.
     * @param config The config object to be given to the MutationObserver.
     */
    constructor(bridge: () => AllyBase, ids: AllyId, callback: (changes: AllyDomChange[], observer: MutationObserver) => void, object: AllyObject, config?: MutationObserverInit|undefined) {
        super(bridge);
        this.id = ids.getId();
        this.observe = true;
        this.callback = callback;
        this.object = object;
        this.startNode = document.createElement("div");
        this.stopNode = document.createElement("div");
        this.destroyer.addCallback(() => {
            this.observer.disconnect();
            ids.returnId(this.id);
            this.startNode.remove();
            this.stopNode.remove();
        });
        this.deTools = new AllyTools();
        this.deCircularLoopProtection = new AllyCircularLoopProtection(this.bridge);
        this.observer = new MutationObserver((changes, observer) => {
            let list: AllyDomChange[] = [];
            changes.forEach((change) => {
                if (change.target === this.startNode) {
                    this.observe = true;
                }
                if (change.target === this.stopNode) {
                    this.observe = false;
                }
                if (this.observe && change.target !== this.startNode && change.target !== this.stopNode) {
                    list.push(change as AllyDomChange);
                }
            });
            if (list.length > 0) {
                this.runOutside(() => {
                    this.deCircularLoopProtection.checkForCircularLoop(this.object.id, this.id);
                    this.callback(list, observer);
                });
            }
        });
        this.observer.observe(this.object.node, config);
        this.observer.observe(this.startNode, {attributes: true});
        this.observer.observe(this.stopNode, {attributes: true});
        let toolsCallback = this.deTools.set(this.object.id, this);
        this.destroyer.addCallback(() => {
            toolsCallback();
        });
    }

    /**
     * Events triggered by the callback provided will be ignored by the MutationObserver of this AllyObserverInstance object.
     * @param callback The callback to be provided.
     */
    public runOutside(callback: () => void) {
        this.pause();
        callback();
        this.resume();
    }

    public pause() {
        this.stopNode.setAttribute("state", "stop");
    }

    public resume() {
        this.startNode.setAttribute("state", "start");
    }
}

/**
 * This class is used to create MutationObserver.
 * 
 * @extends AllyBase
 * @see AllyObserverInstance
 */
export class AllyObserver extends AllyBase {
    /**
     * The list of created AllyObserverInstance objects.
     * 
     * @see AllyObserverInstance
     */
    public observerList: AllyObserverInstance[];
    /**
     * The AllyId object of this AllyObserver object.
     * 
     * @see AllyId
     */
    private ids: AllyId = new AllyId("observer", this.bridge);

    /**
     * @param bridge The bridge to the parent in the hierarchical tree.
     * @see AllyBase
     */
    constructor(bridge: () => AllyBase) {
        super(bridge);
        this.observerList = [];
    }

    /**
     * Creates an AllyObserverInstance object which houses an MutationObserver.
     * @param callback The callback to be executed when an event is detected by the MutationObserver.
     * @param object The object on whose node the MutationObserver should listen.
     * @param config The config object to be given to the MutationObserver.
     * @returns Returns the created AllyObserverInstance object.
     * 
     * @see MutationObserver
     */
    public createObserver(callback: (changes: AllyDomChange[], observer: MutationObserver) => void, object: AllyObject, config?: MutationObserverInit|undefined): AllyObserverInstance {
        let observerInstance = new AllyObserverInstance(this.bridge, this.ids, callback, object, config);
        this.observerList.push(observerInstance);
        return observerInstance;
    }
}